import React from 'react'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'

import {Button, Modal, ModalProps} from '../../sharedComponents/components'
import {colors} from '../../sharedComponents/colors'

import PerformTodoTask from './GeneralTasks/PerformTodoTask'
import PerformTemperatureTask from './GeneralTasks/PerformTemperatureTask'
import PerformHygieneTask from './GeneralTasks/PerformHygieneTask'
import PerformWasteTask from './GeneralTasks/PerformWasteTask'
import PerformSingleChoiceTask from './GeneralTasks/PerformSingleChoiceTask'
import PerformCoolingTask from './GeneralTasks/PerformCoolingTask'
import PerformCoolerCoolingTask from './GeneralTasks/PerformCoolerCoolingTask'
import PerformManualTask from './GeneralTasks/PerformManualTask'
import PerformEquipmentTemperatureTask from './GeneralTasks/PerformEquipmentTemperatureTask'
import PerformDishwasherTemperatureTask from './GeneralTasks/PerformDishwasherTemperatureTask'
import TaskInstruction from './GeneralTasks/TaskInstruction'
import {usePerformingTasks} from './GeneralTasks/usePerformingTasks'
import {normalizeSites, translateTaskGroupName} from '../../config/utils'
import {getTaskUmbrellaColor} from '../home/UnscheduledTaskModal'
import {EventPayload} from 'state/rest'
import {useAppState} from '../../state'
import IsUserHealthInspector from '../../hooks/userIsHealthInspector'
import {isAromiDishTask} from './GeneralTasks/AromiDishSelector'

const hasMeasurementTarget = (suggestions?: string[]): boolean =>
  Boolean(suggestions?.some(suggestion => suggestion?.trim().length))
const hasNumericValue = (value: unknown): boolean => {
  if (typeof value === 'number') return Number.isFinite(value)
  return typeof value === 'string' && /\d/.test(value)
}

const hasAromiDish = (aromiProduct?: string): boolean => Boolean(aromiProduct?.trim().length)

interface ContentModalProps {
  task: any
  payload: EventPayload
  setValue: (key: keyof EventPayload, value: any) => void
  onAssetSaved: () => void
  onAssetRemoved: (assetId: string) => void
  handleSubmit: () => Promise<void>
  handleUpdate: () => Promise<void>
  handleRemove: () => Promise<void>
  handleNumberBlur: (value: string) => void
  handleSubmitAndAddNew?: () => void
  t: any
  hasCurrentEvent: boolean
  currentEventId?: string
  isCompletedByMandatoryForAllTasks: boolean
}

const ContentForModal = ({
  task,
  payload,
  setValue,
  onAssetSaved,
  onAssetRemoved,
  handleSubmit,
  handleRemove,
  handleUpdate,
  handleNumberBlur,
  handleSubmitAndAddNew,
  t,
  hasCurrentEvent,
  currentEventId,
  isCompletedByMandatoryForAllTasks
}: ContentModalProps) => {
  const {state} = useAppState()
  const selectedChain = state.selectedChainId ? (state.chainsById as any)?.[state.selectedChainId] : undefined
  const currentSite =
    state.site?.id && selectedChain
      ? normalizeSites(selectedChain).find((candidate: any) => candidate.id === state.site?.id) || state.site
      : state.site
  let content
  let isInputInvalid = false
  if (task) {
    const reportedActor = payload.reportedActor ?? task.reportedActor
    const hasReportedActor = Array.isArray(reportedActor)
      ? reportedActor.some(v => (v || '').trim().length > 0)
      : (reportedActor || '').trim().length > 0
    const type = task.taskType.id
    const allowAromiAlternative = Boolean(
      currentSite?.aromiIntegration && task?.taskGroup && isAromiDishTask(task.taskGroup.name)
    )
    const invalidMeasurementTarget = allowAromiAlternative
      ? !(hasMeasurementTarget(payload.suggestions) || hasAromiDish(payload.aromiProduct))
      : !hasMeasurementTarget(payload.suggestions)
    payload.incompleteToDo = task.taskType.id === 'todo' && task.subtasks && task.subtasks.length > 0
    switch (type) {
      case 'todo':
        content = (
          <PerformTodoTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleSubmit={handleSubmit}
            handleUpdate={handleUpdate}
            handleRemove={handleRemove}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        break
      case 'temperature':
        content = (
          <PerformTemperatureTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleRemove={handleRemove}
            handleNumberBlur={handleNumberBlur}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = !hasNumericValue(payload.value) || invalidMeasurementTarget
        break
      case 'equipmentTemperature':
        content = (
          <PerformEquipmentTemperatureTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleNumberBlur={handleNumberBlur}
            handleRemove={handleRemove}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = !hasNumericValue(payload.value)
        break
      case 'dishwasherTemperature':
        content = (
          <PerformDishwasherTemperatureTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleRemove={handleRemove}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = !task.dishwasherConfigurations?.every((configuration: any) =>
          configuration.temperatures.every((temperature: any) =>
            payload.measurements?.some(
              measurement =>
                (measurement.dishwasherName === configuration.name ||
                  measurement.dishwasherName.startsWith(`${configuration.name} - `)) &&
                measurement.temperatureType === temperature.type &&
                measurement.value !== '' &&
                measurement.value !== undefined &&
                !Number.isNaN(Number(measurement.value))
            )
          )
        )
        break
      case 'hygiene':
        content = (
          <PerformHygieneTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleNumberBlur={handleNumberBlur}
            handleRemove={handleRemove}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = !hasNumericValue(payload.value)
        break
      case 'waste':
        content = (
          <PerformWasteTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleNumberBlur={handleNumberBlur}
            handleRemove={handleRemove}
            handleSubmitAndAddNew={handleSubmitAndAddNew}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = !hasNumericValue(payload.value)
        break
      case 'singlechoice':
        content = (
          <PerformSingleChoiceTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleRemove={handleRemove}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = !payload.choice
        break
      case 'cooling':
        content = (
          <PerformCoolingTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleUpdate={handleUpdate}
            handleNumberBlur={handleNumberBlur}
            handleRemove={handleRemove}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = !hasNumericValue(payload.value) || invalidMeasurementTarget
        break
      case 'automaticCooling':
        content = (
          <PerformCoolerCoolingTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleRemove={handleRemove}
            handleUpdate={handleUpdate}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = invalidMeasurementTarget
        break
      case 'manual':
        content = (
          <PerformManualTask
            task={task}
            setValue={setValue}
            onAssetSaved={onAssetSaved}
            onAssetRemoved={onAssetRemoved}
            payload={payload}
            handleNumberBlur={handleNumberBlur}
            handleRemove={handleRemove}
            hasCurrentEvent={hasCurrentEvent}
            currentEventId={currentEventId}
          />
        )
        isInputInvalid = !hasNumericValue(payload.value) || invalidMeasurementTarget
        break
      default:
        content = <div>{t('tasks:messages.unsupported', 'Unsupported type')}</div>
        break
    }

    if (isCompletedByMandatoryForAllTasks && !hasReportedActor) {
      isInputInvalid = true
    }
  } else {
    content = null
  }

  return {content, isInputInvalid}
}
interface TaskModalProps extends ModalProps {
  task: any
  onFormSubmit?: () => void
}
export const TaskModal: React.FC<TaskModalProps> = ({isOpen, onClose, task, onFormSubmit, ...rest}) => {
  const {t} = useTranslation()
  const {state} = useAppState()
  const {isHealthInspector} = IsUserHealthInspector()
  const selectedChain = state.selectedChainId ? (state.chainsById as any)?.[state.selectedChainId] : undefined
  const isCompletedByMandatoryForAllTasks = selectedChain?.completedByMandatoryForAllTasks ?? false
  const onSubmit = () => {
    if (onFormSubmit) {
      onFormSubmit()
      return
    }
    onClose?.()
  }

  const initialPayload = {
    task: task,
    choice: '',
    value: task.value ? task.value : undefined, // 0.0 for equipmentTemperature
    measurements: task.measurements || [],
    aromiProduct: task.aromiProduct || undefined,
    reportedActor: undefined,
    relatedSites: task.relatedSites || [],
    assets: task.eventAssets as any, // from cooling task
    remarks: task.notes,
    suggestions: task.suggestions,
    scheduledAt: task.scheduleInstance,
    // ensure samplecollectionDate defaults to today's ISO string for tasks that may use it
    samplecollectionDate: (task as any).samplecollectionDate
      ? (task as any).samplecollectionDate
      : new Date().toISOString(),
    incompleteToDo: undefined,
    ingredients: task.ingredients
  }

  const {
    loading,
    setValue,
    onAssetSaved,
    onAssetRemoved,
    payload,
    handleUpdate,
    handleSubmit,
    handleSubmitAndAddNew,
    handleRemove,
    handleNumberBlur,
    resetTick,
    hasCurrentEvent,
    currentEventId
  } = usePerformingTasks(initialPayload, task.eventId || undefined, onSubmit)

  const {content, isInputInvalid} = ContentForModal({
    task,
    payload,
    setValue,
    onAssetSaved,
    onAssetRemoved,
    handleSubmit,
    handleUpdate,
    handleRemove,
    handleNumberBlur,
    handleSubmitAndAddNew,
    t,
    hasCurrentEvent,
    currentEventId,
    isCompletedByMandatoryForAllTasks
  })

  if (!content) return null
  // include HI in disabled logic so Health Inspectors cannot perform edits
  const disabled = isInputInvalid || loading || isHealthInspector
  const allTodoSubtasksSelected =
    task.taskType.id === 'todo' &&
    Array.isArray(task.subtasks) &&
    task.subtasks.length > 0 &&
    task.subtasks.every((subtask: {name: string}) => (payload.doneSubtasks || []).includes(subtask.name))
  const contentWithDisabled = React.isValidElement(content) ? React.cloneElement(content as any, {disabled}) : content

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translateTaskGroupName(task.taskGroup.name)}
      height="90%"
      maxWidth="1000px"
      headerLeftItem={
        <Button buttonStyle="secondary" buttonSize="small" data-cy="perform-task-cancel" onClick={onClose}>
          {t('common:buttons.cancel', 'Cancel')}
        </Button>
      }
      headerRightItem={
        <div style={{display: 'flex', gap: '0.5rem'}}>
          <Button
            buttonStyle="primary"
            buttonSize="small"
            data-cy="perform-task-save"
            data-testid="submit-button"
            onClick={() =>
              task.status === 'in-progress' ||
              hasCurrentEvent ||
              (task.status === 'active' && task.taskType.id === 'automaticCooling')
                ? handleUpdate()
                : handleSubmit()
            }
            disabled={disabled || allTodoSubtasksSelected}
          >
            {task.taskType.id === 'todo' && task.subtasks && task.subtasks.length > 0
              ? t('tasks:actions.saveIncomplete', 'Save Incomplete')
              : t('common:buttons.save')}
          </Button>
        </div>
      }
      headerBackgroundColor={getTaskUmbrellaColor(task.taskGroup.umbrellaCategory)}
    >
      <ModalContent>
        <InstructionArea>
          <TaskInstruction data={task} />
        </InstructionArea>
        {/* pass cursor style to form area so any non-disabled-aware elements show restricted cursor */}
        <TaskFormArea key={resetTick}>{contentWithDisabled}</TaskFormArea>
      </ModalContent>
    </Modal>
  )
}

const InstructionArea = styled.div`
  display: flex;
  gap: 2rem;
  flex-direction: column;
  background-color: ${colors.system.lightGrey_5};
  border-radius: 1rem;
  height: 100%;
  overflow: auto;
  padding: 1.5rem;
`

const TaskFormArea = styled.div`
  height: 100%;
  overflow: auto;
`

const ModalContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1.5rem;
  justify-content: center;
  padding: 1.5rem;
  height: 90%;
`
